// client/src/components/influencer/InfluencerSNSLink.js
// SNS 계정 연동 (Connect SNS) — Instagram OAuth 전용 화면 (FEATURE_IG_SNS_INTEGRATION 구간 5a)
// InfluencerSNSCheck.js의 Instagram 인증 카드를 이관 + 자동 등록/해제 부수효과 안내(동의 v2)
import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { clearSNSCache } from '../../utils/snsAccountCache';

const API_URL = process.env.REACT_APP_API_URL || '';

// 동의 문구 v2 — 서버 CONSENT_TEXT_V2(InstagramOAuth.js)·개인정보처리방침과 문자 단위 일치 (표시 전용 — 해시는 서버가 계산)
const IG_CONSENT_TEXT_KO = 'Instagram 계정을 연동하면 계정명, 팔로워 수, 게시물 수, 최근 30일 기준 도달·참여 계정 수가 수집됩니다. '
  + '이 지표는 회원님이 자신의 계정 성과를 증빙하기 위해, 회원님이 지원한 캠페인의 광고주에게 선정 진행 기간 동안 공개됩니다. '
  + '지표는 수집일로부터 최대 90일간 보관 후 파기됩니다. '
  + '연동한 계정명과 팔로워 수는 서비스 SNS 계정 목록에 자동 등록되며, 연동 해제 시 계정은 숨김 처리되고 팔로워 수는 초기화됩니다'
  + '(진행 중 계약이 있는 경우 계약 이행 목적으로 일시 보관될 수 있습니다). '
  + '해제 시 이 계정으로 진행 중이던 계약 전 단계의 지원은 자동 취소됩니다.';
const IG_CONSENT_TEXT_EN = 'By connecting your Instagram account, your account name, follower count, media count, and 30-day reach and '
  + 'engaged accounts counts will be collected. These metrics are disclosed to the advertisers of the campaigns you applied to, '
  + 'for the duration of the selection period, so that you can verify your own account performance. '
  + 'Metrics are retained for up to 90 days from the collection date and then destroyed. '
  + 'The connected account name and follower count are automatically registered to your SNS account list. '
  + 'When you disconnect, the account is hidden from the list and the follower count is reset '
  + '(it may be temporarily retained for contract fulfillment if you have an ongoing contract). '
  + 'Upon disconnection, applications that include this account and are not yet contracted will be automatically canceled.';

// ig= 쿼리 안내 문구 (한·영 병기 — 심사관 동선)
// 재연동(restored) 별도 문구는 제거 — "removed account restored" 표현이 데이터 삭제 정책 오해를 살 수 있어 기본 문구로 통일
const IG_RESULT_MESSAGES = {
  connected: 'Instagram 계정이 연동되었습니다. SNS 계정 목록에 자동 등록되었어요. (Connected — the account was registered automatically.)',
  denied: '연동이 취소되었습니다. (Canceled)',
  error: '연동에 실패했습니다. 다시 시도해주세요. (Failed. Please try again.)',
};

function InfluencerSNSLink() {
  const nav = useNavigate();

  // ── Instagram 인증(OAuth) 상태 ──
  const [igConn, setIgConn] = useState(null);           // null = 미연결
  const [loading, setLoading] = useState(true);
  const [igConsentOpen, setIgConsentOpen] = useState(false);
  const [igConsentChecked, setIgConsentChecked] = useState(false);
  const [igConsentSubmitting, setIgConsentSubmitting] = useState(false);
  const [igDisconnectOpen, setIgDisconnectOpen] = useState(false);
  const [igDisconnecting, setIgDisconnecting] = useState(false);

  // 안내 모달
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMsg, setModalMsg] = useState('');
  const openModal = (msg) => { setModalMsg(msg); setModalOpen(true); };

  const fetchIgConn = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/instagram/me`, { credentials: 'include' });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setIgConn(data.connection || null);
        // self-heal로 서버가 SNSInfo를 재등록한 경우 — 목록 캐시 무효화
        if (data.registered) clearSNSCache();
      }
    } catch {} // env 미설정(503) 등 — 미연결 상태 유지
    finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchIgConn(); }, [fetchIgConn]);

  // OAuth 복귀 ig= 쿼리 처리 — 안내 모달 후 ig·restored만 제거(sub=snsLink 유지)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ig = params.get('ig');
    if (!ig) return;
    if (ig === 'connected') {
      openModal(IG_RESULT_MESSAGES.connected); // restored 파라미터는 무시 — 항상 동일한 연동 완료 문구
      clearSNSCache(); // 자동 등록 반영 — SNS 목록 캐시 무효화
      fetchIgConn();
    } else {
      openModal(IG_RESULT_MESSAGES[ig] || IG_RESULT_MESSAGES.error);
    }
    params.delete('ig');
    params.delete('restored');
    const qs = params.toString();
    window.history.replaceState(null, '', `${window.location.pathname}${qs ? `?${qs}` : ''}`);
  }, [fetchIgConn]); // eslint-disable-line react-hooks/exhaustive-deps

  // [동의하고 계속] → 동의 기록(v2) → OAuth 시작 (전체 페이지 이동)
  const handleIgConsentContinue = async () => {
    setIgConsentSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/instagram/consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ agreed: true, version: 'v2' }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        window.location.href = `${API_URL}/api/instagram/oauth/start`;
        return; // 페이지 이탈 — 상태 복원 불필요
      }
      setIgConsentSubmitting(false);
      setIgConsentOpen(false);
      openModal(IG_RESULT_MESSAGES.error);
    } catch {
      setIgConsentSubmitting(false);
      setIgConsentOpen(false);
      openModal(IG_RESULT_MESSAGES.error);
    }
  };

  // [연동 해제] 확인 → DELETE → 미연결 복귀 (+ 취소된 지원 건수 안내)
  const handleIgDisconnect = async () => {
    setIgDisconnecting(true);
    try {
      const res = await fetch(`${API_URL}/api/instagram/connection`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) {
        setIgConn(null);
        setIgDisconnectOpen(false);
        clearSNSCache(); // 숨김 처리 반영 — SNS 목록 캐시 무효화
        const n = Number(data.canceledCount || 0);
        // Instagram 쪽 권한 회수 API는 미제공(Meta 정책) — 완전 회수 경로를 사용자에게 안내
        openModal(`연동이 해제되었습니다. 취소된 지원: ${n}건. Instagram 설정 > 앱 및 웹사이트에서 SelfAd-IG의 접근 권한도 제거할 수 있습니다. (Disconnected. Canceled applications: ${n}. You can also fully revoke access in Instagram Settings > Apps and websites.)`);
      } else {
        setIgDisconnectOpen(false);
        openModal('해제에 실패했습니다. 다시 시도해주세요. (Failed. Please try again.)');
      }
    } catch {
      setIgDisconnectOpen(false);
      openModal('해제에 실패했습니다. 다시 시도해주세요. (Failed. Please try again.)');
    } finally {
      setIgDisconnecting(false);
    }
  };

  // 연결 카드 지표 라인 (null 지표 미표시 — 0 더미 금지)
  const igMetricParts = (c) => {
    const parts = [];
    if (Number.isFinite(c.followersCount)) parts.push(<span key="f">팔로워 (Followers) <b>{c.followersCount.toLocaleString()}명</b></span>);
    if (Number.isFinite(c.mediaCount)) parts.push(<span key="m">게시물 (Posts) <b>{c.mediaCount.toLocaleString()}</b></span>);
    if (Number.isFinite(c.reach)) parts.push(<span key="r">최근 30일 도달 (30-day Reach) <b>{c.reach.toLocaleString()}</b></span>);
    if (Number.isFinite(c.accountsEngaged)) parts.push(<span key="e">참여 (Engaged) <b>{c.accountsEngaged.toLocaleString()}</b></span>);
    return parts;
  };

  if (loading) {
    return (
      <div className="mb-card" style={{ padding: 40, textAlign: 'center', color: '#6b7686', fontSize: 14, fontWeight: 600 }}>
        불러오는 중...
      </div>
    );
  }

  return (
    <div>
      <div className="mb-page-header">
        <h2 className="mb-page-title">SNS 계정 연동 (Connect SNS)</h2>
        <p className="mb-page-description">
          SNS 계정을 연동하면 계정 소유가 인증되고, 팔로워·도달 지표로 내 계정 성과를 광고주에게 직접 증빙할 수 있어요.
          (Connect your SNS account to verify ownership and prove your performance to advertisers.)
        </p>
      </div>

      {/* ── Instagram 연동 카드 ── */}
      <div className="mb-card" style={{ marginBottom: 14, padding: '18px 20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <h3 className="mb-section-title" style={{ margin: 0 }}>Instagram 연동 (Instagram)</h3>
          {igConn && (
            <span style={{
              display: 'inline-block', padding: '3px 10px', borderRadius: 999,
              background: '#e6f9f1', color: '#0b7a54', fontSize: 12.5, fontWeight: 800,
            }}>
              연동 인증됨 (Connected via Instagram)
            </span>
          )}
        </div>

        {igConn ? (
          <>
            <div style={{ marginTop: 10, fontSize: 14, color: '#3b4658', display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              <b style={{ color: '#0b1324' }}>@{igConn.username}</b>
              {igMetricParts(igConn)}
              {igConn.collectedAt && igMetricParts(igConn).length > 0 && (
                <span style={{ color: '#6b7686' }}>{String(igConn.collectedAt).slice(0, 10)} 기준 (as of)</span>
              )}
            </div>
            <p className="mb-page-description" style={{ margin: '10px 0 0' }}>
              SNS 계정 목록에 등록되어 있어요. 희망 단가는 [SNS 계정 · 단가]에서 입력할 수 있어요.
              (This account is registered in your SNS account list. You can set desired rates in [SNS Accounts &amp; Rates].)
            </p>
            <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button
                type="button"
                className="mb-btn mb-btn-secondary mb-btn-sm"
                onClick={() => nav('/influencer/mybiz?sub=snsView')}
              >
                SNS 계정 · 단가로 이동 (Go to Rates)
              </button>
              <button
                type="button"
                className="mb-btn mb-btn-secondary mb-btn-sm"
                onClick={() => setIgDisconnectOpen(true)}
              >
                연동 해제 (Disconnect)
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="mb-page-description" style={{ margin: '8px 0 12px' }}>
              Instagram 계정을 연동하면 계정 소유가 인증되고, SNS 계정 목록에 자동 등록됩니다.
              (Connecting verifies ownership and registers the account automatically.)
            </p>
            <button
              type="button"
              onClick={() => { setIgConsentChecked(false); setIgConsentOpen(true); }}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 10,
                padding: '11px 18px', border: 'none', borderRadius: 10, cursor: 'pointer',
                background: 'linear-gradient(45deg, #f09433, #e6683c, #dc2743, #cc2366, #bc1888)',
                color: '#fff', fontSize: 14.5, fontWeight: 800,
              }}
            >
              <img src="/images/sns/instagram-login.svg" alt="" style={{ width: 20, height: 20 }} />
              Instagram으로 계속하기 (Continue with Instagram)
            </button>
          </>
        )}
      </div>

      {/* 다른 플랫폼 안내 */}
      <div className="mb-card" style={{ padding: '18px 20px' }}>
        <h3 className="mb-section-title" style={{ margin: 0 }}>다른 플랫폼 (Other Platforms)</h3>
        <p className="mb-page-description" style={{ margin: '8px 0 12px' }}>
          블로그·유튜브·틱톡 계정은 [새 SNS 계정 추가]에서 등록 신청할 수 있어요. (Blog, YouTube, and TikTok accounts can be registered via [Add SNS Account].)
        </p>
        <button
          type="button"
          className="mb-btn mb-btn-secondary mb-btn-sm"
          onClick={() => nav('/influencer/mybiz?sub=snsAdd')}
        >
          새 SNS 계정 추가하기 (Add SNS Account)
        </button>
      </div>

      {/* 안내 모달 */}
      {modalOpen && (
        <div className="mbm-overlay" onClick={() => setModalOpen(false)}>
          <div className="mb-card mbm" style={{ width: 380, maxWidth: 'calc(100vw - 32px)', margin: 0, textAlign: 'center', padding: '32px' }} onClick={e => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: 15.5, color: '#0b1324', lineHeight: 1.6 }}>{modalMsg}</p>
            <div className="mbm-foot">
              <button className="mb-btn mb-btn-primary" onClick={() => setModalOpen(false)}>확인 (OK)</button>
            </div>
          </div>
        </div>
      )}

      {/* Instagram 연동 동의 모달 — 체크박스 동의 (v2) */}
      {igConsentOpen && (
        <div className="mbm-overlay" onClick={() => !igConsentSubmitting && setIgConsentOpen(false)}>
          <div
            className="mb-card mbm"
            style={{ width: 480, maxWidth: 'calc(100vw - 32px)', margin: 0, padding: '28px', maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <h3 style={{ margin: '0 0 12px', fontSize: 17, color: '#0b1324' }}>
              Instagram 계정 연동 동의 (Consent to Connect Instagram)
            </h3>
            <p style={{ margin: '0 0 10px', fontSize: 14, color: '#3b4658', lineHeight: 1.7 }}>
              {IG_CONSENT_TEXT_KO}
            </p>
            <p style={{ margin: '0 0 14px', fontSize: 12.5, color: '#6b7686', lineHeight: 1.6 }}>
              {IG_CONSENT_TEXT_EN}
              <br />
              (English translation for reference — the Korean text prevails)
            </p>
            <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13.5, fontWeight: 700, color: '#0b1324', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={igConsentChecked}
                onChange={e => setIgConsentChecked(e.target.checked)}
                style={{ width: 17, height: 17, marginTop: 2, accentColor: '#0fb37a' }}
              />
              위 내용에 동의합니다 (I agree to the above)
            </label>
            <div className="mbm-foot" style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 18 }}>
              <button
                className="mb-btn mb-btn-secondary"
                onClick={() => setIgConsentOpen(false)}
                disabled={igConsentSubmitting}
              >
                취소 (Cancel)
              </button>
              <button
                className="mb-btn mb-btn-primary"
                onClick={handleIgConsentContinue}
                disabled={!igConsentChecked || igConsentSubmitting}
              >
                {igConsentSubmitting ? '이동 중... (Redirecting...)' : '동의하고 계속 (Agree & Continue)'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Instagram 연동 해제 확인 모달 */}
      {igDisconnectOpen && (
        <div className="mbm-overlay" onClick={() => !igDisconnecting && setIgDisconnectOpen(false)}>
          <div className="mb-card mbm" style={{ width: 440, maxWidth: 'calc(100vw - 32px)', margin: 0, textAlign: 'center', padding: '32px' }} onClick={e => e.stopPropagation()}>
            <p style={{ margin: 0, fontSize: 15, color: '#0b1324', lineHeight: 1.6 }}>
              Instagram 연동을 해제하시겠습니까?
              <br />
              <span style={{ fontSize: 13, color: '#6b7686' }}>
                해제하면 자동 등록된 계정이 목록에서 숨겨지고, 그 계정이 포함된 진행 전 단계의 지원이 취소됩니다(광고주 화면에 취소 안내가 표시됩니다). 이미 계약된 프로젝트는 유지됩니다.
                (The linked account will be hidden from your SNS list; applications that include this account and are not yet contracted will be canceled — advertisers will see a cancellation notice; accepted contracts remain.)
              </span>
            </p>
            <div className="mbm-foot" style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 18 }}>
              <button className="mb-btn mb-btn-secondary" onClick={() => setIgDisconnectOpen(false)} disabled={igDisconnecting}>
                취소 (Cancel)
              </button>
              <button className="mb-btn mb-btn-primary" onClick={handleIgDisconnect} disabled={igDisconnecting}>
                {igDisconnecting ? '해제 중...' : '연동 해제 (Disconnect)'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default InfluencerSNSLink;
